/**
 * Formulaire de demande de garantie de loyer
 * Multi-étapes avec calcul automatique de prime
 * Basé sur le modèle Automate/UNIQA
 */
import React, { useState, useEffect } from 'react';
import { Calculator, User, Check, ArrowRight, ArrowLeft, Send } from 'lucide-react';
import { StepObjetLoue, StepBailleur, StepColocataireConsent, StepConfirmation } from './GarantieLoyerSteps';
import API_BASE_URL from '../../../config/api';
import { loadUserDataCache, saveUserDataCache } from '../../../utils/userDataCache';

// Constantes de calcul (même taux qu'Automate)
const TAUX_PRIME = 0.036; // 3.6%
const TAXE = 0.05; // 5% taxe

// Calcul de la prime annuelle
const calculerPrime = (montant) => {
  if (!montant || montant <= 0) return 0;
  const primeHT = montant * TAUX_PRIME;
  const primeTTC = primeHT * (1 + TAXE);
  return Math.round(primeTTC * 100) / 100;
};

// Validation helpers
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

const isValidPhone = (phone) => {
  // Allow Swiss phone formats: 079 123 45 67, +41 79 123 45 67, 0791234567
  const phoneRegex = /^(\+41|0)?[\s.-]?\d{2}[\s.-]?\d{3}[\s.-]?\d{2}[\s.-]?\d{2}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

const isValidNPA = (npa) => {
  // Swiss NPA is 4 digits
  return /^\d{4}$/.test(npa);
};

// Composant FormField HORS du composant principal pour éviter les re-renders
const FormField = React.memo(({ label, type = 'text', value, onChange, required = false, placeholder = '', options = null, error = null }) => (
  <div className="mb-4">
    <label className="block text-sm text-gray-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {options ? (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none ${error ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-blue-400'}`}
        required={required}
      >
        <option value="" className="bg-gray-800">Sélectionner...</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value} className="bg-gray-800">{opt.label}</option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${error ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-blue-400'}`}
      />
    )}
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
));

// Load initial form data with shared user cache
const getInitialFormData = () => {
  const cached = loadUserDataCache();
  return {
    // Étape 1 - Calcul
    montantGarantie: '',
    loyerMensuel: '',
    
    // Étape 2 - Locataire (pre-filled from shared cache)
    locataire: {
      civilite: '',
      prenom: cached.prenom || '',
      nom: cached.nom || '',
      dateNaissance: cached.dateNaissance || cached.date_naissance || '',
      adresse: cached.adresse || '',
      npa: cached.npa || '',
      ville: cached.ville || '',
      email: cached.email || '',
      telephone: cached.telephone || ''
    },
    
    // Étape 3 - Objet loué
    objetLoue: {
      adresse: '',
      npa: '',
      ville: '',
      reference: '',
      dateDebutBail: '',
      dateDebutPolice: ''
    },
    
    // Étape 4 - Bailleur
    bailleur: {
      type: 'particulier', // particulier ou regie
      nomSociete: '',
      prenom: '',
      nom: '',
      adresse: '',
      npa: '',
      ville: '',
      email: '',
      telephone: ''
    },
    
    // Étape 5 - Co-locataire (optionnel)
    colocataire: {
      hasColocataire: false,
      prenom: '',
      nom: '',
      dateNaissance: ''
    },
    
    // Consentements (never cached)
    accepteConditions: false,
    accepteTraitement: false
  };
};

const GarantieLoyerForm = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // État du formulaire
  const [formData, setFormData] = useState(getInitialFormData);

  // Save locataire data to shared cache when it changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const loc = formData.locataire;
      if (loc.nom || loc.prenom || loc.email) {
        saveUserDataCache({
          nom: loc.nom,
          prenom: loc.prenom,
          email: loc.email,
          telephone: loc.telephone,
          dateNaissance: loc.dateNaissance,
          adresse: loc.adresse,
          npa: loc.npa,
          ville: loc.ville
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.locataire]);

  // Mise à jour des champs - fonction simple sans useCallback
  const updateField = (section, field, value) => {
    setFormData(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
  };

  // Prime calculée
  const primeAnnuelle = calculerPrime(parseFloat(formData.montantGarantie) || 0);
  const primeMensuelle = Math.round((primeAnnuelle / 12) * 100) / 100;

  // Validation par étape
  const validateStep1 = () => {
    const errors = {};
    if (!formData.loyerMensuel || parseFloat(formData.loyerMensuel) <= 0) {
      errors.loyerMensuel = 'Le loyer mensuel doit être supérieur à 0';
    }
    if (!formData.montantGarantie || parseFloat(formData.montantGarantie) <= 0) {
      errors.montantGarantie = 'Le montant de la garantie est requis';
    }
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    const loc = formData.locataire;
    
    if (!loc.civilite) errors.civilite = 'La civilité est requise';
    if (!loc.prenom?.trim()) errors.prenom = 'Le prénom est requis';
    if (!loc.nom?.trim()) errors.nom = 'Le nom est requis';
    if (!loc.dateNaissance) {
      errors.dateNaissance = 'La date de naissance est requise';
    } else if (!isValidDate(loc.dateNaissance)) {
      errors.dateNaissance = 'Format de date invalide';
    }
    if (!loc.adresse?.trim()) errors.adresse = 'L\'adresse est requise';
    if (!loc.npa?.trim()) {
      errors.npa = 'Le NPA est requis';
    } else if (!isValidNPA(loc.npa)) {
      errors.npa = 'Le NPA doit contenir 4 chiffres';
    }
    if (!loc.ville?.trim()) errors.ville = 'La ville est requise';
    if (!loc.email?.trim()) {
      errors.email = 'L\'e-mail est requis';
    } else if (!isValidEmail(loc.email)) {
      errors.email = 'Format d\'e-mail invalide';
    }
    if (!loc.telephone?.trim()) {
      errors.telephone = 'Le téléphone est requis';
    } else if (!isValidPhone(loc.telephone)) {
      errors.telephone = 'Format de téléphone invalide (ex: 079 123 45 67)';
    }
    
    return errors;
  };

  const validateStep3 = () => {
    const errors = {};
    const obj = formData.objetLoue;
    
    if (!obj.adresse?.trim()) errors.adresseObjet = 'L\'adresse du bien est requise';
    if (!obj.npa?.trim()) {
      errors.npaObjet = 'Le NPA est requis';
    } else if (!isValidNPA(obj.npa)) {
      errors.npaObjet = 'Le NPA doit contenir 4 chiffres';
    }
    if (!obj.ville?.trim()) errors.villeObjet = 'La ville est requise';
    if (!obj.dateDebutBail) {
      errors.dateDebutBail = 'La date de début du bail est requise';
    } else if (!isValidDate(obj.dateDebutBail)) {
      errors.dateDebutBail = 'Format de date invalide';
    }
    if (!obj.dateDebutPolice) {
      errors.dateDebutPolice = 'La date de début de la police est requise';
    } else if (!isValidDate(obj.dateDebutPolice)) {
      errors.dateDebutPolice = 'Format de date invalide';
    }
    
    return errors;
  };

  const validateStep4 = () => {
    const errors = {};
    const bail = formData.bailleur;
    
    if (!bail.type) errors.typeBailleur = 'Le type de bailleur est requis';
    if (bail.type === 'regie' && !bail.nomSociete?.trim()) {
      errors.nomSociete = 'Le nom de la société est requis';
    }
    if (!bail.prenom?.trim()) errors.prenomBailleur = 'Le prénom est requis';
    if (!bail.nom?.trim()) errors.nomBailleur = 'Le nom est requis';
    if (!bail.adresse?.trim()) errors.adresseBailleur = 'L\'adresse est requise';
    if (!bail.npa?.trim()) {
      errors.npaBailleur = 'Le NPA est requis';
    } else if (!isValidNPA(bail.npa)) {
      errors.npaBailleur = 'Le NPA doit contenir 4 chiffres';
    }
    if (!bail.ville?.trim()) errors.villeBailleur = 'La ville est requise';
    if (!bail.email?.trim()) {
      errors.emailBailleur = 'L\'e-mail est requis';
    } else if (!isValidEmail(bail.email)) {
      errors.emailBailleur = 'Format d\'e-mail invalide';
    }
    if (!bail.telephone?.trim()) {
      errors.telephoneBailleur = 'Le téléphone est requis';
    } else if (!isValidPhone(bail.telephone)) {
      errors.telephoneBailleur = 'Format de téléphone invalide (ex: 079 123 45 67)';
    }
    
    return errors;
  };

  const validateStep5 = () => {
    const errors = {};
    
    if (formData.colocataire.hasColocataire) {
      if (!formData.colocataire.prenom?.trim()) errors.prenomColoc = 'Le prénom du co-locataire est requis';
      if (!formData.colocataire.nom?.trim()) errors.nomColoc = 'Le nom du co-locataire est requis';
      if (!formData.colocataire.dateNaissance) {
        errors.dateNaissanceColoc = 'La date de naissance est requise';
      } else if (!isValidDate(formData.colocataire.dateNaissance)) {
        errors.dateNaissanceColoc = 'Format de date invalide';
      }
    }
    
    if (!formData.accepteConditions) {
      errors.accepteConditions = 'Vous devez accepter les conditions générales';
    }
    
    return errors;
  };

  const validateCurrentStep = () => {
    let errors = {};
    switch (step) {
      case 1: errors = validateStep1(); break;
      case 2: errors = validateStep2(); break;
      case 3: errors = validateStep3(); break;
      case 4: errors = validateStep4(); break;
      case 5: errors = validateStep5(); break;
      default: break;
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation avec validation
  const nextStep = () => {
    if (validateCurrentStep()) {
      setStep(s => Math.min(s + 1, 6));
    }
  };
  const prevStep = () => {
    setValidationErrors({});
    setStep(s => Math.max(s - 1, 1));
  };

  // Soumission
  const handleSubmit = async () => {
    // Validate step 5 before submitting
    if (!validateCurrentStep()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/garantie-loyer/demande`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          primeAnnuelle,
          primeMensuelle
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de l\'envoi');
      }
      
      setSubmitSuccess(true);
      setStep(6);
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Indicateur d'étapes
  const StepIndicator = () => (
    <div className="flex justify-center mb-8">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
            step >= s ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400'
          }`}>
            {step > s ? <Check size={16} /> : s}
          </div>
          {s < 5 && <div className={`w-12 h-0.5 ${step > s ? 'bg-blue-500' : 'bg-white/10'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="glass p-8 rounded-2xl max-w-2xl mx-auto">
      {step < 6 && <StepIndicator />}
      
      {/* Étape 1: Calcul */}
      {step === 1 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="text-blue-400" size={28} />
            <h3 className="text-2xl font-bold text-white">Calcul de votre prime</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-2">
                Loyer mensuel (CHF) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={formData.loyerMensuel}
                onChange={(e) => updateField(null, 'loyerMensuel', e.target.value)}
                onBlur={(e) => {
                  const loyer = parseFloat(e.target.value);
                  if (loyer && loyer > 0) {
                    updateField(null, 'montantGarantie', String(loyer * 3));
                  }
                }}
                placeholder="Ex: 1500"
                required
                className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${validationErrors.loyerMensuel ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-blue-400'}`}
              />
              <p className="text-xs text-gray-500 mt-1">La garantie sera calculée automatiquement (3 mois)</p>
              {validationErrors.loyerMensuel && <p className="text-red-400 text-xs mt-1">{validationErrors.loyerMensuel}</p>}
            </div>
            <FormField
              label="Montant de la garantie (CHF)"
              type="number"
              value={formData.montantGarantie}
              onChange={(v) => updateField(null, 'montantGarantie', v)}
              required
              placeholder="Ex: 4500 (3 mois)"
              error={validationErrors.montantGarantie}
            />
          </div>
          
          {formData.montantGarantie && (
            <div className="mt-6 p-6 bg-gradient-to-r from-blue-900/40 to-transparent rounded-xl border border-blue-500/30">
              <h4 className="text-lg font-semibold text-white mb-4">Résultat</h4>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-400">Prime annuelle</p>
                  <p className="text-2xl font-bold text-blue-400">CHF {primeAnnuelle.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Prime mensuelle</p>
                  <p className="text-2xl font-bold text-green-400">CHF {primeMensuelle.toFixed(2)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">Taux: 3.6% + 5% taxe fédérale</p>
            </div>
          )}
        </div>
      )}

      {/* Étape 2: Locataire */}
      {step === 2 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <User className="text-blue-400" size={28} />
            <h3 className="text-2xl font-bold text-white">Informations du locataire</h3>
          </div>
          
          <FormField
            label="Civilité"
            value={formData.locataire.civilite}
            onChange={(v) => updateField('locataire', 'civilite', v)}
            required
            options={[
              { value: 'M.', label: 'Monsieur' },
              { value: 'Mme', label: 'Madame' }
            ]}
            error={validationErrors.civilite}
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Prénom"
              value={formData.locataire.prenom}
              onChange={(v) => updateField('locataire', 'prenom', v)}
              required
              error={validationErrors.prenom}
            />
            <FormField
              label="Nom"
              value={formData.locataire.nom}
              onChange={(v) => updateField('locataire', 'nom', v)}
              required
              error={validationErrors.nom}
            />
          </div>
          
          <FormField
            label="Date de naissance"
            type="date"
            value={formData.locataire.dateNaissance}
            onChange={(v) => updateField('locataire', 'dateNaissance', v)}
            required
            error={validationErrors.dateNaissance}
          />
          
          <FormField
            label="Adresse actuelle"
            value={formData.locataire.adresse}
            onChange={(v) => updateField('locataire', 'adresse', v)}
            required
            error={validationErrors.adresse}
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="NPA"
              value={formData.locataire.npa}
              onChange={(v) => updateField('locataire', 'npa', v)}
              required
              placeholder="Ex: 2000"
              error={validationErrors.npa}
            />
            <FormField
              label="Ville"
              value={formData.locataire.ville}
              onChange={(v) => updateField('locataire', 'ville', v)}
              required
              error={validationErrors.ville}
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="E-mail"
              type="email"
              value={formData.locataire.email}
              onChange={(v) => updateField('locataire', 'email', v)}
              required
              error={validationErrors.email}
            />
            <FormField
              label="Téléphone"
              type="tel"
              value={formData.locataire.telephone}
              onChange={(v) => updateField('locataire', 'telephone', v)}
              required
              placeholder="Ex: 079 123 45 67"
              error={validationErrors.telephone}
            />
          </div>
        </div>
      )}

      {/* Étape 3: Objet loué */}
      {step === 3 && (
        <StepObjetLoue formData={formData} updateField={updateField} validationErrors={validationErrors} />
      )}

      {/* Étape 4: Bailleur */}
      {step === 4 && (
        <StepBailleur formData={formData} updateField={updateField} validationErrors={validationErrors} />
      )}

      {/* Étape 5: Co-locataire + Consentements */}
      {step === 5 && (
        <StepColocataireConsent formData={formData} updateField={updateField} validationErrors={validationErrors} />
      )}

      {/* Étape 6: Confirmation */}
      {step === 6 && (
        <StepConfirmation onClose={onClose} />
      )}

      {/* Navigation buttons at bottom */}
      {step < 6 && (
        <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
          {step > 1 ? (
            <button onClick={prevStep} className="btn-glass px-6 py-3 flex items-center gap-2">
              <ArrowLeft size={18} /> Précédent
            </button>
          ) : <div />}
          
          {step < 5 ? (
            <button onClick={nextStep} className="btn-glass btn-primary px-6 py-3 flex items-center gap-2">
              Suivant <ArrowRight size={18} />
            </button>
          ) : step === 5 && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.accepteConditions}
              className="btn-glass btn-primary px-6 py-3 flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Envoi...' : 'Envoyer la demande'} <Send size={18} />
            </button>
          )}
        </div>
      )}
      
      {submitError && (
        <p className="text-red-400 text-sm mt-4 text-center">{submitError}</p>
      )}
    </div>
  );
};

export default GarantieLoyerForm;
